import { HttpResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LegalsService } from '@services/legals/legals.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { I18nService } from '../../../../../../i18n/services';
import { AppCluster, AppKeyService, IAppKey } from '../../../../../../services/appkey';
import { AuthService } from '../../../../../../services/auth';
import { DialogClass, DialogService } from '../../../../../../services/dialog';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '../../../../../../services/feedback';
import { ProgressIndicatorService } from '../../../../../../services/progress';
import { ICompany } from '../../../../../../services/company';
import { ISortedAppTerm, TermState, TermType } from '../../../../../../services/legals';
import { LegalAppEditDialogComponent } from '../legal-app-edit-dialog';
import { LegalAppKeyAssociation, LegalAppKeyAssociationDialogComponent } from '../legal-appkey-association-dialog';
import { LegalNewDialogComponent } from '../legal-new-dialog';

@Component({
	selector: 'app-legals-table-app',
	templateUrl: './legals-table-app.component.html'
})
export class LegalsTableAppComponent implements OnInit, OnChanges {
	@Input() company: ICompany;
	@Input() editable: boolean;

	public legalsFiltered: ISortedAppTerm[] = [];
	public legals: ISortedAppTerm[] = [];
	public loading = false;
	public clusters: AppCluster[] = [];
	public appKeys: IAppKey[] = [];
	public type = TermType.app;
	public appKeySelect = new FormControl('');
	public termState = TermState;

	constructor(
		private legalsService: LegalsService,
		private appKeyService: AppKeyService,
		private i18nService: I18nService,
		private authService: AuthService,
		private dialogService: DialogService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService
	) {}

	public ngOnInit(): void {
		this.getClusters();

		this.appKeySelect.valueChanges.subscribe(() => this.filterLegals());
	}

	public ngOnChanges(): void {
		this.getTerms();
		this.getAppKeys();
	}

	public getTerms(force = false): void {
		this.loading = true;

		this.resetLegals();
		this.getAppKeys();

		this.legalsService
			.getOathAppLegals(true, force)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe((result: ISortedAppTerm[]) => {
				this.legals = result;
				this.filterLegals();
			});
	}

	public editLegal(term: ISortedAppTerm): void {
		this.dialogService.open(LegalAppEditDialogComponent, {
			data: {
				company: this.company,
				cluster: null,
				terms: term,
				editable: this.editable,
				type: this.type,
				callback: (refreshTerms: boolean) => {
					this.getTerms(refreshTerms);
				}
			}
		});
	}

	public confirmDeleteLegal(term: ISortedAppTerm): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Are you sure you want to delete this document?', 'app.prompt.delete-document'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.deleteTerm(term);
				}
			}
		});
	}

	public viewAppKeyAssociation(term: ISortedAppTerm): void {
		const data: LegalAppKeyAssociation = {
			company: this.company,
			terms: term,
			editable: this.editable,
			callback: () => {
				this.getTerms(false);
			}
		};

		this.dialogService.open(LegalAppKeyAssociationDialogComponent, {
			panelClass: DialogClass.large,
			data
		});
	}

	public addLegal(): void {
		this.dialogService.open(LegalNewDialogComponent, {
			data: {
				company: this.company,
				cluster: null,
				type: this.type,
				legal: {
					active: false,
					locale: this.authService?.accountUser.locale ? this.authService?.accountUser.locale : 'en'
				},
				callback: (added: boolean) => {
					if (added) {
						this.getTerms(true);
					}
				}
			}
		});
	}

	public viewLegal(term: ISortedAppTerm, version: number, state = TermState.draft): void {
		if (term.documents[version]?.filename) {
			this.progressIndicatorService.spinner(true);

			this.legalsService
				.getTermData(term.termsId, version, this.type)
				.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
				.subscribe((res: HttpResponse<string>) => {
					this.legalsService.serveDocument(
						this.legalsService.getAppTermDocument(term, version),
						res,
						this.editable,
						this.type,
						state,
						() => {}
					);
				});
		} else if (term.documents[version]?.url) {
			this.legalsService.serveExternalDocument(term.documents[version].url);
		}
	}

	public getClusterName(clusterId: string): string {
		const cluster = this.clusters.find((item) => item.clusterId === clusterId);
		return cluster ? cluster.name : clusterId;
	}

	private resetLegals(): void {
		this.legals = [];
	}

	private getAppKeys(): void {
		this.appKeyService.getAll().subscribe((res: { [clientId: string]: IAppKey }) => {
			this.appKeys = Object.values(res)
				.sort((a, b) => {
					return a.name.localeCompare(b.name);
				})
				.filter((appKey) => appKey.requiredTerms.length > 0);
		});
	}

	private getClusters(): void {
		this.appKeyService.getClusters().subscribe((res: AppCluster[]) => {
			this.clusters = res;
		});
	}

	private filterLegals(): void {
		const requiredTerms = this.appKeySelect.value ? this.appKeySelect.value.requiredTerms.map((term) => term.termsId) : [];

		this.legalsFiltered = this.legals
			.filter((term) => {
				return requiredTerms.length > 0 ? requiredTerms.includes(term.termsId) : true;
			})
			.sort((a, b) => {
				return a.displayName.localeCompare(b.displayName);
			});
	}

	private deleteTerm(term: ISortedAppTerm): void {
		this.progressIndicatorService.spinner(true);

		const requests = {};

		Object.keys(term.documents).forEach((version) => {
			requests[version] = this.legalsService.deleteDraftTerms(term.termsId, version);
		});

		forkJoin(requests).subscribe(
			() => {
				this.getTerms(false);
				this.progressIndicatorService.spinner(false);
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
			}
		);
	}
}
